import { mutate } from 'swr';
import { get } from '@/utils/queries';
import { patch } from '@/utils/api';

const settingsRoute = 'settings';

// One labeled row of the config table, as GET /api/settings (api/routers/settingsRouter.ts) returns
// it. valueType says how the page edits the value; rows with editable false are shown read-only.
export interface SettingRow {
  key: string;
  value: unknown;
  label: string;
  description: string | null;
  section: string | null;
  valueType: 'switch' | 'number_list' | 'text_list' | null;
  editable: boolean;
}

export interface SettingsResponse {
  settings: SettingRow[];
}

// Sends one change, then reloads the settings so the page shows what is saved now. `expected` is
// the value the page showed; the api refuses the save when the database holds something else. The
// settings are reloaded after a failed save too, so a refused save leaves the page showing the
// current value. A blank reason is left out so the event_logs row does not store an empty note.
const saveConfig = async (
  path: string,
  value: unknown,
  expected: unknown,
  note?: string
) => {
  const trimmedNote = note?.trim();
  try {
    await patch(
      `${settingsRoute}/${path}`,
      trimmedNote ? { value, expected, note: trimmedNote } : { value, expected }
    );
  } finally {
    await mutate(settingsRoute);
  }
};

const updateConfigSwitch = (
  key: string,
  value: boolean,
  expected: unknown,
  note?: string
) => saveConfig(`config/${encodeURIComponent(key)}`, value, expected, note);

// Replaces the whole list with `value`.
const updateConfigList = (
  key: string,
  value: (number | string)[],
  expected: unknown,
  note?: string
) =>
  saveConfig(`config/${encodeURIComponent(key)}/list`, value, expected, note);

function useSettings() {
  // Reloads when the person comes back to the tab, so the page is less likely to show values that
  // changed elsewhere since it loaded.
  const settings = get<SettingsResponse>(settingsRoute, {
    revalidateOnFocus: true,
  });
  return { settings, updateConfigSwitch, updateConfigList };
}

export default useSettings;
